import type { AccountSummary } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { Pressable, TouchableWithoutFeedback } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    CalendarIcon,
    Checkbox,
    CheckCircleIcon,
    CircleIcon,
    FootnoteRegular,
    HelpCircleIcon,
    HolisticAccountLogo,
    Icon,
    type IconType,
    isWeb,
    RadioInput,
    RepeatIcon,
    Separator,
    StoreIcon,
    tokens,
    TypeIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import { formatDollars } from '../../../common/utils/bankingUtils';
import {
    convertCategoryToIcon,
    getBillTitle,
    getCadenceDisplayString,
    getCategoryDisplayString,
} from '../../../common/utils/billUtils';
import { billsAccountsAtom } from '../../state/BillsAtoms';
import {
    BillCategory,
    BillCategoryBackgroundColors,
    type BillingCadence,
    type IBill,
    type PartialBill,
} from '../../types/Bill';
import { BillDetailIcon, BillIconSize } from './BillIcons';

export const TEXT_FOR_UNSET_VALUES = 'Unknown';

const BaseDetailRow = ({
    icon,
    labelText,
    bodyText,
    labelPosition = 'top',
}: {
    icon: React.JSX.Element | IconType;
    labelText: string;
    bodyText: string;
    labelPosition?: 'top' | 'bottom';
}) => {
    const iconComponent =
        'props' in icon ? (
            icon
        ) : (
            <BillDetailIcon
                icon={icon}
                color="$gray100"
                size={BillIconSize.SMALL}
            />
        );
    return (
        <XStack gap={16} alignItems="center" flex={1}>
            {iconComponent}
            <YStack paddingVertical={4} flex={1}>
                {labelPosition === 'bottom' ? (
                    <>
                        <BodyMediumEmphasized numberOfLines={1}>
                            {bodyText}
                        </BodyMediumEmphasized>
                        <BodySmallRegular color="$gray1000" numberOfLines={1}>
                            {labelText}
                        </BodySmallRegular>
                    </>
                ) : (
                    <>
                        <FootnoteRegular numberOfLines={1}>
                            {labelText}
                        </FootnoteRegular>
                        <BodyMediumEmphasized numberOfLines={1}>
                            {bodyText}
                        </BodyMediumEmphasized>
                    </>
                )}
            </YStack>
        </XStack>
    );
};

export const BillCadenceRow = ({ cadence }: { cadence?: BillingCadence }) => {
    const bodyText =
        cadence !== null && cadence !== undefined
            ? getCadenceDisplayString(cadence)
            : TEXT_FOR_UNSET_VALUES;
    return (
        <BaseDetailRow
            icon={RepeatIcon}
            labelText="Repeats"
            bodyText={bodyText}
        />
    );
};

export const BillTimingRow = ({
    timingDescription,
}: {
    timingDescription?: string;
}) => {
    timingDescription =
        timingDescription !== undefined && !isEmpty(timingDescription)
            ? timingDescription
            : TEXT_FOR_UNSET_VALUES;
    return (
        <BaseDetailRow
            icon={CalendarIcon}
            labelText="Timing"
            bodyText={timingDescription}
        />
    );
};

export const BillCategoryRow = ({
    categoryDescription,
}: {
    categoryDescription?: string;
}) => {
    const text =
        categoryDescription !== undefined && !isEmpty(categoryDescription)
            ? getCategoryDisplayString(categoryDescription)
            : TEXT_FOR_UNSET_VALUES;

    const category =
        categoryDescription !== undefined && !isEmpty(categoryDescription)
            ? (categoryDescription as BillCategory)
            : BillCategory.OTHER;
    const icon = convertCategoryToIcon(category) ?? HelpCircleIcon;
    const color =
        BillCategoryBackgroundColors[category] ??
        tokens.color.light.gray.gray100;
    const iconComponent = (
        <BillDetailIcon color={color} icon={icon} size={BillIconSize.SMALL} />
    );
    return (
        <BaseDetailRow
            icon={iconComponent}
            labelText="Category"
            bodyText={text}
        />
    );
};

type BillAccountRowViewType = 'account_selection' | 'bill_details';

interface RowPropsWithAccountId {
    accountId: string | null;
    viewType: BillAccountRowViewType;
}

interface RowPropsWithAccount {
    account: AccountSummary;
    viewType: BillAccountRowViewType;
}

type RowProps = RowPropsWithAccountId | RowPropsWithAccount;

export const BillAccountRow = ({ viewType, ...props }: RowProps) => {
    const billsAccounts = useAtomValue(billsAccountsAtom);

    let account: AccountSummary | undefined;
    if ('account' in props) {
        account = props.account;
    } else if ('accountId' in props) {
        account = billsAccounts?.find((a) => a.id === props.accountId);
    }

    const name = account?.name ?? TEXT_FOR_UNSET_VALUES;
    const institution = account?.institutionName ?? '';
    const logo = account?.logo;
    const provider = account?.provider;
    const accountNumber = account?.accountNumberMasked ?? '';
    let labelPosition: 'top' | 'bottom' = 'top';
    const textProps = {
        bodyText: '',
        labelText: '',
    };

    if (viewType === 'bill_details') {
        textProps.bodyText = `${name} ${institution} ${accountNumber}`;
        textProps.labelText = 'Account';
    }
    if (viewType === 'account_selection') {
        labelPosition = 'bottom';
        textProps.bodyText = name;
        textProps.labelText = `${institution} *${accountNumber}`;
    }

    const iconComponent = (
        <HolisticAccountLogo
            institutionName={institution}
            logo={logo}
            size="md"
            provider={provider}
        />
    );

    return (
        <BaseDetailRow
            icon={iconComponent}
            labelPosition={labelPosition}
            {...textProps}
        />
    );
};

export const BillDisplayNameRow = ({
    bill,
    useGenericIcon = false,
}: {
    bill: PartialBill;
    useGenericIcon?: boolean;
}) => {
    const iconComponent = useGenericIcon ? (
        <BillDetailIcon
            icon={TypeIcon}
            color="$peach300"
            size={BillIconSize.SMALL}
        />
    ) : (
        <BillDetailIcon
            icon={bill?.icon}
            color={bill?.color}
            size={BillIconSize.SMALL}
        />
    );
    return (
        <BaseDetailRow
            icon={iconComponent}
            bodyText={bill?.displayName ?? bill.payee ?? TEXT_FOR_UNSET_VALUES}
            labelText="Name"
        />
    );
};

export const BillPayeeRow = ({
    bill,
    useGenericIcon = false,
}: {
    bill: PartialBill;
    useGenericIcon: boolean;
}) => {
    const iconComponent = useGenericIcon ? (
        <BillDetailIcon
            icon={StoreIcon}
            color="$peach300"
            size={BillIconSize.SMALL}
        />
    ) : (
        <BillDetailIcon
            icon={bill?.icon}
            color={bill?.color}
            size={BillIconSize.SMALL}
        />
    );
    return (
        <BaseDetailRow
            icon={iconComponent}
            bodyText={bill?.payee ?? TEXT_FOR_UNSET_VALUES}
            labelText="Payee"
        />
    );
};

export const BillRow = ({
    bill,
    isSelected,
    onSelect,
}: {
    bill: IBill;
    isSelected: boolean;
    onSelect: () => void;
}) => {
    const { icon, color, amount, category, cadence } = bill;

    const billTitle = getBillTitle(bill);
    const billCategory = getCategoryDisplayString(category);
    const cadenceText = getCadenceDisplayString(cadence);
    const amountText = formatDollars(amount ?? 0, false);
    const iconColor = color ?? tokens.color.light.gray.gray100;

    const iconComponent = (
        <BillDetailIcon
            icon={icon}
            color={iconColor}
            size={BillIconSize.SMALL}
        />
    );

    return (
        <XStack
            alignItems="center"
            maxWidth={652}
            width={isWeb ? 'calc(100vw - 48px)' : '100%'}
            flexShrink={1}
        >
            <XStack gap={16} flexShrink={1} flexGrow={1}>
                <View flexShrink={0}>{iconComponent}</View>
                <XStack alignItems="center" gap={8} flexGrow={1} flexShrink={1}>
                    <YStack gap={4}>
                        <BodyMediumEmphasized
                            ellipsizeMode="tail"
                            numberOfLines={1}
                        >
                            {billTitle}
                        </BodyMediumEmphasized>
                        <BodySmallRegular color="$gray1000">
                            {billCategory}
                        </BodySmallRegular>
                    </YStack>
                </XStack>
            </XStack>
            <XStack alignItems="flex-start" justifyContent="flex-end" gap={16}>
                <YStack gap={4} alignItems="flex-end">
                    <BodyMediumRegular>{amountText}</BodyMediumRegular>
                    <BodySmallRegular color="$gray1000">
                        {cadenceText}
                    </BodySmallRegular>
                </YStack>

                <YStack onPress={onSelect} cursor="pointer">
                    <Pressable
                        hitSlop={10}
                        onPress={(e) => {
                            e.stopPropagation?.(); // for web
                            onSelect();
                        }}
                    >
                        {isSelected ? (
                            <Icon
                                color="$green900"
                                icon={CheckCircleIcon}
                                size={24}
                            />
                        ) : (
                            <Icon
                                color="$gray1100"
                                icon={CircleIcon}
                                size={24}
                            />
                        )}
                    </Pressable>
                </YStack>
            </XStack>
        </XStack>
    );
};

export const SelectableRow = ({
    rowContentComponent,
    isFirstRow,
    isSelected,
    isMultiSelect,
    onSelect,
}: {
    rowContentComponent: React.JSX.Element;
    isFirstRow: boolean;
    isSelected: boolean;
    isMultiSelect: boolean;
    onSelect: () => void;
}) => {
    return (
        <TouchableWithoutFeedback onPress={onSelect}>
            <YStack cursor="pointer">
                {isFirstRow && isMultiSelect && <Separator />}
                <XStack
                    paddingVertical={8}
                    paddingHorizontal={6}
                    alignItems="center"
                    justifyContent="space-between"
                    borderRadius={12}
                    borderWidth={1}
                    borderColor={
                        !isMultiSelect && isSelected
                            ? '$green900'
                            : 'transparent'
                    }
                    backgroundColor={
                        !isMultiSelect && isSelected
                            ? '$green200'
                            : 'transparent'
                    }
                >
                    {rowContentComponent}
                    {isMultiSelect ? (
                        <Checkbox
                            id={`checkbox-row-${JSON.stringify(rowContentComponent.props)}`}
                            checked={isSelected}
                            tone="positive"
                            onPress={(e) => {
                                e?.stopPropagation();
                                onSelect();
                            }}
                        />
                    ) : (
                        <RadioInput
                            isSelected={isSelected}
                            tone="positive"
                            onPress={(e) => {
                                e?.stopPropagation();
                                onSelect();
                            }}
                        />
                    )}
                </XStack>
                {isMultiSelect && <Separator />}
            </YStack>
        </TouchableWithoutFeedback>
    );
};
